import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PanelState, ToastNotification, UserPreferences, PlaceCategory } from '@/types';

interface UIState {
  // Panel visibility
  panels: PanelState;

  // Mobile detection
  isMobile: boolean;

  // Sidebar collapsed state
  sidebarCollapsed: boolean;

  // Notifications
  notifications: ToastNotification[];

  // Loading states
  isInitializing: boolean;

  // Active category for nearby-places discovery (driven by the header quick-chips)
  nearbyCategory: PlaceCategory | null;

  // User preferences
  preferences: UserPreferences;

  // Auth modal (login / sign-up) - mounted as a modal overlay, driven from here
  authModalOpen: boolean;
  authModalMode: 'login' | 'signup';

  // Actions
  openAuthModal: (mode?: 'login' | 'signup') => void;
  closeAuthModal: () => void;
  togglePanel: (panel: keyof PanelState) => void;
  openPanel: (panel: keyof PanelState) => void;
  closePanel: (panel: keyof PanelState) => void;
  closeAllPanels: () => void;
  setIsMobile: (isMobile: boolean) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  addNotification: (notification: Omit<ToastNotification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  setIsInitializing: (initializing: boolean) => void;
  setNearbyCategory: (category: PlaceCategory | null) => void;
  updatePreferences: (preferences: Partial<UserPreferences>) => void;
}

const defaultPreferences: UserPreferences = {
  defaultMapStyle: 'default',
  defaultRouteProfile: 'driving',
  unitSystem: 'metric',
  language: 'en',
  show3DBuildings: true,
  voiceGender: 'default',
};

const defaultPanels: PanelState = {
  searchPanel: true,
  directionsPanel: false,
  placeDetailsPanel: false,
  layersPanel: false,
  offlinePanel: false,
  settingsPanel: false,
  nearbyPanel: false,
  savedPlacesPanel: false,
  measurePanel: false,
  streetViewPanel: false,
  shortcutsPanel: false,
  accountPanel: false,
  developersPanel: false,
  geofencesPanel: false,
  embedBuilderPanel: false,
  addressPanel: false,
  logisticsPanel: false,
  adminPanel: false,
  matchingPanel: false,
  dataOverlayPanel: false,
  styleStudioPanel: false,
  staticMapPanel: false,
  fleetPanel: false,
  deliveryPlannerPanel: false,
};

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      // Initial state
      panels: defaultPanels,
      isMobile: false,
      sidebarCollapsed: false,
      notifications: [],
      isInitializing: true,
      nearbyCategory: null,
      preferences: defaultPreferences,
      authModalOpen: false,
      authModalMode: 'login',

      // Actions
      openAuthModal: (mode = 'login') => set((state) => ({
        authModalOpen: true,
        authModalMode: mode,
        // Don't let the account panel sit behind the modal.
        panels: { ...state.panels, accountPanel: false },
      })),

      closeAuthModal: () => set({ authModalOpen: false }),

      // Toggling a panel OPEN closes the others (they render as the same bottom
      // sheet on mobile and would otherwise stack on the exact same spot / overlap
      // on desktop); toggling it closed just closes it. Search may stay open.
      togglePanel: (panel) => set((state) => {
        if (state.panels[panel]) {
          return { panels: { ...state.panels, [panel]: false } };
        }
        const newPanels = { ...defaultPanels };
        newPanels[panel] = true;
        if (panel !== 'searchPanel') {
          newPanels.searchPanel = state.panels.searchPanel;
        }
        return { panels: newPanels };
      }),

      openPanel: (panel) => set((state) => {
        // Close other panels when opening a new one (except search which can stay open)
        const newPanels = { ...defaultPanels };
        newPanels[panel] = true;
        if (panel !== 'searchPanel') {
          newPanels.searchPanel = state.panels.searchPanel;
        }
        return { panels: newPanels };
      }),

      closePanel: (panel) => set((state) => ({
        panels: {
          ...state.panels,
          [panel]: false,
        },
      })),

      closeAllPanels: () => set({ panels: defaultPanels }),

      setIsMobile: (isMobile) => set({ isMobile }),

      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

      addNotification: (notification) => set((state) => {
        const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newNotification: ToastNotification = {
          ...notification,
          id,
          duration: notification.duration ?? 5000,
        };
        return {
          notifications: [...state.notifications, newNotification],
        };
      }),

      removeNotification: (id) => set((state) => ({
        notifications: state.notifications.filter((n) => n.id !== id),
      })),

      clearNotifications: () => set({ notifications: [] }),

      setIsInitializing: (initializing) => set({ isInitializing: initializing }),

      setNearbyCategory: (category) => set({ nearbyCategory: category }),

      updatePreferences: (newPreferences) => set((state) => ({
        preferences: { ...state.preferences, ...newPreferences },
      })),
    }),
    {
      name: 'tensormaps-ui-store',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        preferences: state.preferences,
      }),
    }
  )
);
